import { useCallback } from "react";
import { useNavigate, type NavigateOptions, type To } from "react-router-dom";

export interface SafeNavigate {
  (to: To, options?: NavigateOptions): void;
  (delta: number): void;
}

/**
 * react-router-dom v7's navigate() returns a Promise (view-transition support),
 * whereas v6 returned void. Most call sites in this app fire-and-forget navigation,
 * so this wraps useNavigate() to keep the void signature and explicitly swallow
 * any rejection (e.g. an aborted view transition) instead of letting it surface
 * as an unhandled promise rejection.
 */
export const useSafeNavigate = (): SafeNavigate => {
  const navigate = useNavigate();
  const navigateWithUnknownResult = navigate as unknown as {
    (to: To, options?: NavigateOptions): unknown;
    (delta: number): unknown;
  };

  return useCallback(
    (toOrDelta: To | number, options?: NavigateOptions): void => {
      if (typeof toOrDelta === "number") {
        Promise.resolve(navigateWithUnknownResult(toOrDelta)).catch(() => {});
        return;
      }

      if (options === undefined) {
        Promise.resolve(navigateWithUnknownResult(toOrDelta)).catch(() => {});
        return;
      }

      Promise.resolve(navigateWithUnknownResult(toOrDelta, options)).catch(
        () => {}
      );
    },
    [navigateWithUnknownResult]
  ) as SafeNavigate;
};
